import React, {useState} from "react";
import {useSelector} from "react-redux";
import LoadingComponent from "../../components/LoadingComponent";
import {useTranslation} from "react-i18next";
import {CompletePayroll} from "../../app/interfaces";
import {useNavigate} from "react-router-dom";
import {fullNameForUser} from "../../utils";
import {
    TableDataCell,
    TableRow,
    TablePrimaryText,
    TableSecondaryText,
    TableHeaderCell
} from "../../components/Table/styles";
import {
    Table,
    TableBody,
    TableHeader,
    TableNavigation
} from "../../components/Table";
import {
    DataTableLayoutFooter,
    DataTableLayoutHeader,
    DataTableLayoutTitle
} from "../../layouts/DataTableLayout/styles";

import DataTableLayout, {EmptyDataTableLayout} from "../../layouts/DataTableLayout/DataTableLayout";
import useFetchPayrolls from "../../hooks/useFetchPayrolls";
import {pendingPayrollStatuses} from "../../utils/constants";
import {FlexColumn, StatusPill} from "../../components";
import {allPayrollsFetchError, allPayrollsFetchState, selectedPayrollsPaginated} from "../../app/payroll/selectors";
import {selectAdminUser} from "../../app/adminUser/selectors";

interface ActiveEmployeesProps {
    itemsPerPage: number;
}

export const PendingPayrolls = ({itemsPerPage}: ActiveEmployeesProps) => {

    useFetchPayrolls()

    const [currentPage, setCurrentPage] = useState<number>(1)

    const payrolls = useSelector(
        selectedPayrollsPaginated(
            currentPage,
            itemsPerPage,
            pendingPayrollStatuses,
            null,
            null
        )
    );

    const fetchState = useSelector(allPayrollsFetchState)
    const error = useSelector(allPayrollsFetchError)
    const adminUser = useSelector(selectAdminUser)

    const navigate = useNavigate();
    const {t} = useTranslation();

    const dataTableTitle = t('pending_payrolls')

    if (fetchState === 'loading' || fetchState === 'idle') {
        return <EmptyDataTableLayout title={dataTableTitle}>
            <LoadingComponent/>
        </EmptyDataTableLayout>
    }

    if (error) {
        console.error(error)
        return <EmptyDataTableLayout title={dataTableTitle}>
            {error}
        </EmptyDataTableLayout>
    }

    if (!payrolls.items.length) {
        return <EmptyDataTableLayout title={dataTableTitle}>
            {t('no_pending_payrolls') as string}
        </EmptyDataTableLayout>
    }

    return <DataTableLayout>
        <DataTableLayoutHeader>
            <DataTableLayoutTitle>
                {dataTableTitle}
            </DataTableLayoutTitle>
        </DataTableLayoutHeader>
        <Table>
            <TableHeader>
                <TableHeaderCell>{t('employee')}</TableHeaderCell>
                <TableHeaderCell style={{textAlign: 'center'}}>{t('status')}</TableHeaderCell>
            </TableHeader>
            <TableBody>
                {payrolls.items.map((p, i) => PayrollRow(p, i, navigate, t, adminUser.locale))}
            </TableBody>
        </Table>
        <DataTableLayoutFooter>
            <TableNavigation
                pageLimit={itemsPerPage}
                summary={payrolls.summary}
                setPage={setCurrentPage}
            />
        </DataTableLayoutFooter>
    </DataTableLayout>
}

const PayrollRow = (payroll: CompletePayroll, i: number, navigate: Function, t: Function, locale: string = 'en') => {
    const payrollDate = new Date(payroll.created).toLocaleDateString(locale, {month: 'short', year:'numeric', timeZone: 'UTC'})

    return <TableRow
        key={i}
        clickable={true}
        onClick={() => navigate(`/payroll/${payroll._id}`)}
    >
        <TableDataCell>
            <FlexColumn>
                <TablePrimaryText>{fullNameForUser(payroll.eor_employee)}</TablePrimaryText>
                <TableSecondaryText>{`${payrollDate} - ${payroll.company.name}`}</TableSecondaryText>
            </FlexColumn>
        </TableDataCell>
        <TableDataCell style={{textAlign: "center"}}>
            <FlexColumn align="center" justify="center" style={{height: '100%'}}>
                <StatusPill
                    status={payroll.status}
                >
                    {t(payroll.status)}
                </StatusPill>
            </FlexColumn>
        </TableDataCell>
    </TableRow>
}

export default PendingPayrolls;
